import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import axios from "../../api/axios";
import { getAttributeValue, getDataByTypes } from "../../utils/utils";
import APRComment from "./APRComment.js/AprComments";
import APRmemo from "./APRComment.js/APRmemo";

const PROJECT_PROGRAM = "g3wMUKEMmH3";
const PROJECT_TYPE_ATTRIBUTE = "wtf3BR2YO8w";
const PROJECT_TYPE_VALUE = "Project";
const IMPLEMENTATION_STAGE = "rRWa5ghYO35";
const EXPENDITURE_ELEMENT = "jr8gk707kAw";
const OBSERVATION_ELEMENT = "tprVkQQg1wm";

const MAINTENANCE_TYPES = [
  "Rehabilitation",
  "Extension",
  "Spot",
  "Improvement",
  "Spot Improvement",
];

function getStageEventValue(reports, trackedEntity, stageId, dataElementId) {
  const stageEvent = reports.find(
    (report) =>
      report.trackedEntity === trackedEntity
      && report.programStage === stageId
      && Array.isArray(report.dataValues)
      && report.dataValues.length > 0,
  );

  if (!stageEvent) {
    return "";
  }

  const match = stageEvent.dataValues.find(
    (dataValue) => dataValue.dataElement === dataElementId,
  );

  return match?.value ?? "";
}

function getEventValue(reports, trackedEntity, dataElementId) {
  for (const report of reports) {
    if (report.trackedEntity !== trackedEntity || !Array.isArray(report.dataValues)) {
      continue;
    }

    const match = report.dataValues.find(
      (dataValue) => dataValue.dataElement === dataElementId,
    );

    if (match) {
      return match.value ?? "";
    }
  }

  return "";
}

function getActualRelease(project) {
  const actualRelease = getAttributeValue("Actual Release", project);
  if (actualRelease !== "N/A") {
    return actualRelease;
  }

  return getAttributeValue("Actual Released", project);
}

const Table_7 = forwardRef(({ year, district, period, hideTableDis }, ref) => {
  const [tableData, setTableData] = useState([]);

  const selectedYear = useMemo(() => {
    const parsed = parseInt(year, 10);
    return Number.isFinite(parsed) ? parsed : new Date().getFullYear();
  }, [year]);

  useEffect(() => {
    getProjects();
  }, [selectedYear, district, period]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "Table_7",
      tableData,
    }),
  }));

  async function getProjects() {
    if (!district) {
      setTableData([]);
      return;
    }

    try {
      const startDate = `${selectedYear}-01-01`;
      const endDate = `${selectedYear}-12-31`;

      const [entitiesResp, eventsResp] = await Promise.all([
        axios.get(
          `/tracker/trackedEntities?orgUnit=${district}&program=${PROJECT_PROGRAM}&filter=${PROJECT_TYPE_ATTRIBUTE}:EQ:${PROJECT_TYPE_VALUE}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
        ),
        axios.get(
          `/tracker/events?program=${PROJECT_PROGRAM}&orgUnit=${district}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
        ),
      ]);

      const projects = entitiesResp.data.instances || [];
      const reports = eventsResp.data.instances || [];
      const maintenanceProjects = getDataByTypes(projects, "Project Focus", MAINTENANCE_TYPES) || [];

      if (maintenanceProjects.length === 0) {
        setTableData([]);
        return;
      }

      const temps = maintenanceProjects.map((project, idx) => {
        const estimatedCost = getAttributeValue("Estimated Cost", project);
        const actualReleased = getActualRelease(project);
        const expenditureRaw = getStageEventValue(
          reports,
          project.trackedEntity,
          IMPLEMENTATION_STAGE,
          EXPENDITURE_ELEMENT,
        );
        const observation = getEventValue(reports, project.trackedEntity, OBSERVATION_ELEMENT);

        const estimatedCostValue = parseFloat(estimatedCost) || 0;
        const actualReleasedValue = parseFloat(actualReleased) || 0;

        return {
          no: idx + 1,
          description: getAttributeValue("Description", project),
          location: getAttributeValue("Location", project),
          maintenance: getAttributeValue("Project Focus", project),
          estimatedCost,
          actualReleased,
          gap: estimatedCostValue - actualReleasedValue,
          expenditure: expenditureRaw || 0,
          observation,
        };
      });

      setTableData(temps);
    } catch (err) {
      console.error("Error in getProjects:", err);
      setTableData([]);
    }
  }

  return (
    <div className="col-12">
      <h3>Table 7 - Repair and maintenance of Existing Infrastructure</h3>
      <div className="card">
        <div className="card-header" />
        <div className="card-body">
          <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table6-${year}`}
            hideTableDis={hideTableDis}
          />

          <div className="table-responsive">
            <table className="table table-bordered">
              <thead style={{ backgroundColor: "#d4edda", fontWeight: "bold", border: "1px solid #000" }}>
                <tr>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>S/N</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Asset/Infrastructure</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Location</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Type of Maintenance</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Estimated Cost</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Actual Release</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Gap</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Expenditure</th>
                  <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Recommendation</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((row, index) => (
                    <tr key={index}>
                      <td style={{ border: "1px solid #000" }}>{row.no}</td>
                      <td style={{ border: "1px solid #000" }}>{row.description}</td>
                      <td style={{ border: "1px solid #000" }}>{row.location}</td>
                      <td style={{ border: "1px solid #000" }}>{row.maintenance}</td>
                      <td style={{ border: "1px solid #000" }}>{row.estimatedCost}</td>
                      <td style={{ border: "1px solid #000" }}>{row.actualReleased}</td>
                      <td style={{ border: "1px solid #000" }}>{row.gap}</td>
                      <td style={{ border: "1px solid #000" }}>{row.expenditure}</td>
                      <td style={{ border: "1px solid #000" }}>{row.observation}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" style={{ border: "1px solid #000", textAlign: "center" }}>
                      No data available for {selectedYear}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table7-${year}`}
            hideTableDis={hideTableDis}
          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
});

export default Table_7;
